// src/components/Header.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { getCart } from "../services/cartService";
import api from "../services/api";

interface NavProductType {
  id: number;
  name: string;
}

interface CategoryWithTypes {
  id: number;
  name: string;
  productTypes: NavProductType[];
}

const Header: React.FC = () => {
  const [categories, setCategories] = useState<CategoryWithTypes[]>([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<number | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState<number | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  /* -------------------- NAVIGATION HELPER -------------------- */
  const handleNavigate = (path: string) => {
    setOpenDropdown(false);
    setHoveredCategoryId(null);
    setMobileNavOpen(false);
    setMobileExpandedCategory(null);

    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* -------------------- DATA FETCHING -------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/categories/with-product-types");
        setCategories(res.data);
      } catch (err) {
        console.error(err);
      }
    })();

    const fetchCartCount = async () => {
      try {
        const res = await getCart();
        const items = (res?.cart || res) ?? [];
        const total = Array.isArray(items)
          ? items.reduce((s, i) => s + (i.quantity || 0), 0)
          : 0;
        setCartCount(total);
      } catch {}
    };

    fetchCartCount();
    window.addEventListener("cartUpdated", fetchCartCount as EventListener);
    return () =>
      window.removeEventListener("cartUpdated", fetchCartCount as EventListener);
  }, []);

  /* -------------------- RENDER -------------------- */
  return (
    <header className="sticky top-0 z-50 bg-[#f5ebe0] shadow-sm">
      {/* Top bar */}
      <div className="flex items-center justify-between max-w-7xl mx-auto px-6 py-3">
		{/* Logo */} 
		<Link to="/" className="flex-shrink-0"> 
			<img src="/logo.png" alt="Beautonde" className="h-16 object-contain" /> 
		</Link>

        {/* Desktop Search */}
        <form
          className="hidden md:flex w-1/2 max-w-xl"
          onSubmit={(e) => {
            e.preventDefault();
            if (!searchTerm.trim()) return;
            handleNavigate(`/shop?q=${encodeURIComponent(searchTerm.trim())}`);
          }}
        >
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-l-md focus:ring-1 focus:ring-red-100"
          />
          <button className="px-4 text-sm bg-black text-white rounded-r-md">
            Search
          </button>
        </form>

        {/* Desktop Cart */}
        <button
          className="hidden md:flex items-center gap-2 relative"
          onClick={() => handleNavigate("/cart")}
        >
          CART <ShoppingCartIcon className="h-6 w-6" />
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 bg-black text-white text-xs rounded-full flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>

        {/* Mobile Hamburger + Cart */}
        <div className="md:hidden flex items-center gap-4">
          <button className="relative" onClick={() => handleNavigate("/cart")}>
            <ShoppingCartIcon className="h-6 w-6" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 bg-black text-white text-xs rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>

          <button
            className="flex flex-col gap-1"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
          >
            <span className="w-6 h-0.5 bg-black" />
            <span className="w-6 h-0.5 bg-black" />
            <span className="w-6 h-0.5 bg-black" />
          </button>
        </div>
      </div>

      {/* Desktop Nav */}
      <nav className="hidden md:flex justify-center border-t border-b border-neutral-200">
        {/* Shop All */}
        <div
          className="relative"
          onMouseEnter={() => setOpenDropdown(true)}
          onMouseLeave={() => setOpenDropdown(false)}
        >
          <button className="uppercase text-sm py-3 px-8 hover:bg-white">Shop all</button>
          {openDropdown && (
            <div className="absolute left-0 w-[700px] bg-white shadow-lg z-40">
              <div className="px-10 py-8 grid grid-cols-4 gap-10">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <h3 className="mb-3 text-sm font-semibold uppercase border-b pb-2">
                      {cat.name}
                    </h3>
                    <ul className="space-y-2">
                      {cat.productTypes.map((type) => (
                        <li key={type.id}>
                          <button
                            onClick={() => handleNavigate(`/shop?type=${type.id}`)}
                            className="text-sm uppercase text-neutral-700 hover:text-black text-left w-full"
                          >
                            {type.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Individual Category Dropdowns */}
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="relative"
            onMouseEnter={() => setHoveredCategoryId(cat.id)}
            onMouseLeave={() => setHoveredCategoryId(null)}
          >
            <button className="uppercase text-sm py-3 px-8 hover:bg-white">{cat.name}</button>
            {hoveredCategoryId === cat.id && cat.productTypes.length > 0 && (
              <div className="absolute left-0 bg-white shadow-lg z-50 min-w-[220px]">
                {cat.productTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleNavigate(`/shop?type=${type.id}`)}
                    className="block w-full text-left px-4 py-2 text-sm uppercase hover:bg-neutral-100"
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Mobile Nav */}
	{mobileNavOpen && (
	<div className="md:hidden border-t px-4 py-3">
		{categories.map((cat) => {
		const isExpanded = mobileExpandedCategory === cat.id;
		return (
			<div key={cat.id} className="mb-2">
			<button
				className="w-full flex justify-between items-center py-2 uppercase font-medium text-left"
				onClick={() =>
				setMobileExpandedCategory(isExpanded ? null : cat.id)
				}
			>
				<span>{cat.name}</span>
				{cat.productTypes.length > 0 && (
				<span className="text-xl font-bold">
					{isExpanded ? "–" : "+"}
				</span>
				)}
			</button>

			{isExpanded && (
				<div className="pl-4">
				{cat.productTypes.map((type) => (
					<button
					key={type.id}
					onClick={() => handleNavigate(`/shop?type=${type.id}`)}
					className="block py-1 text-sm uppercase text-neutral-700 text-left w-full"
					>
					{type.name}
					</button>
				))}
				</div>
			)}
			</div>
		);
		})}
	</div>
	)}
    </header>
  );
};

export default Header;
